import { FeatureFlag } from '@ps/web-core-enums';
import type { Countries } from '@ps/web-core-intl';
import { useFeature } from '@ps/web-core-store';
import { Input } from '@ps/web-shared-components';
import type { AnyObject } from 'final-form';
import React, { useState } from 'react';
import { useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import type { AddressDetails } from '@swf/components/AddressResults/AddressResults';
import AddressResults from '@swf/components/AddressResults/AddressResults';
import { select } from '@swf/core/store';
import { ADDRESS_MAX_LENGTH, useAddressFieldValidators } from '@swf/first';
import countries from '@swf/generated/countries.json';
import states from '@swf/generated/states.json';

import { TXTCLI_Address } from './StreetAddressDefault.messages.json';
import styles from './StreetAddressDefault.scss';

interface Props {
  disabled: boolean;
}

const StreetAddressDefault: React.FC<Props> = ({ disabled }) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const country = useSelector(select.account.getCountry) as string;
  const accountMenuAddressSearch = useFeature(FeatureFlag.AccountMenuAddressSearch);
  const form = useForm();
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value || '');
  const countryStates = country && states[country];
  const onDismiss = () => setSearchQuery('');
  const registeredFields = form?.getRegisteredFields();
  const validators = useAddressFieldValidators();

  const onResponse = (items: AddressDetails[]) => {
    onDismiss();
    return registeredFields?.forEach((field) => {
      if (field === 'streetAddress') {
        form?.change('streetAddress', items[0].address);
      } else if (field === 'country') {
        const countryName = (countries as Countries).find(
          ({ value }) => value === items[0].country
        )?.text;
        form?.change('country', countryName);
      } else if (field === 'state') {
        form.change(
          field,
          countryStates.find(
            (state: AnyObject) => state.text === items[0][field] || state.value === items[0][field]
          )?.value
        );
      } else {
        form.change(field, items[0][field as keyof AddressDetails]);
      }
    });
  };

  return (
    <>
      <Input
        id="streetAddress"
        label={TXTCLI_Address}
        disabled={disabled}
        maxLength={ADDRESS_MAX_LENGTH}
        validators={validators}
        onChange={onChange}
      />
      {accountMenuAddressSearch &&
        searchQuery.length > 3 &&
        form.getState().dirtyFields.streetAddress && (
          <div className={styles.lookup}>
            <AddressResults
              searchQuery={searchQuery}
              onResponse={onResponse}
              onDismiss={onDismiss}
              className={styles.addressResults}
            />
          </div>
        )}
    </>
  );
};

export default StreetAddressDefault;
